import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Wrench, Trash2, FlaskRound, Eye } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const moneyFmt = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Correcciones() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [creamLots, setCreamLots] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [targetType, setTargetType] = useState("milk");
  const [form, setForm] = useState({ reception_id: "", final_result: "" });
  const [rows, setRows] = useState([{ code: "", kg: "" }]);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [c, r, cl, p] = await Promise.all([
      api.get("/corrections"), api.get("/receptions"), api.get("/cream-curd-receptions?product=cream"), api.get("/parameters")
    ]);
    setItems(c.data);
    setReceptions(r.data);
    setCreamLots(cl.data);
    setCatalog(p.data.correction_inputs || []);
  };
  useEffect(() => { load(); }, []);

  const lotOptions = targetType === "cream" ? creamLots : receptions;

  const catalogByCode = useMemo(() => {
    const m = {};
    catalog.forEach((c) => { m[c.code] = c; });
    return m;
  }, [catalog]);

  const computedRows = useMemo(() => rows.map((r) => {
    const insumo = catalogByCode[r.code];
    const kg = parseFloat(r.kg) || 0;
    const price = insumo?.price_per_kg ?? 0;
    return { ...r, name: insumo?.name || "", price, cost: kg * price };
  }), [rows, catalogByCode]);

  const total = useMemo(() => computedRows.reduce((s, r) => s + (r.cost || 0), 0), [computedRows]);

  const updateRow = (i, field, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: val };
    setRows(next);
  };
  const addRow = () => setRows([...rows, { code: "", kg: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reception_id) return toast.error("Selecciona el lote");
    const validItems = computedRows.filter((r) => r.code && parseFloat(r.kg) > 0);
    if (validItems.length === 0) return toast.error("Agrega al menos un insumo con cantidad");
    try {
      await api.post("/corrections", {
        reception_id: form.reception_id,
        target_type: targetType,
        final_result: form.final_result,
        items: validItems.map((r) => ({ code: r.code, kg: parseFloat(r.kg) })),
      });
      toast.success(`Corrección registrada · costo ${moneyFmt(total)}`);
      setForm({ reception_id: "", final_result: "" });
      setRows([{ code: "", kg: "" }]);
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar"); }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="correcciones-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900">Correcciones</h1>
          <p className="text-sm text-stone-500 mt-1">Baja la acidez de leche o crema con insumos del catálogo. El sistema calcula el costo automáticamente con el precio configurado en Parámetros.</p>
        </div>
        <Button onClick={() => setOpen(!open)} data-testid="new-correction-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
          <Plus className="w-4 h-4 mr-1.5" /> Nueva corrección
        </Button>
      </div>

      {open && (
        <Card className="p-6 border-stone-200 bg-white shadow-sm">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">¿Qué vas a corregir?</Label>
              <div className="mt-1.5 inline-flex rounded-lg border border-stone-200 p-1 bg-stone-50">
                <button type="button" data-testid="target-type-milk" onClick={() => { setTargetType("milk"); setForm({ ...form, reception_id: "" }); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${targetType === "milk" ? "bg-[#1E3A8A] text-white" : "text-stone-600 hover:bg-stone-100"}`}>
                  Leche
                </button>
                <button type="button" data-testid="target-type-cream" onClick={() => { setTargetType("cream"); setForm({ ...form, reception_id: "" }); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${targetType === "cream" ? "bg-[#1E3A8A] text-white" : "text-stone-600 hover:bg-stone-100"}`}>
                  Crema
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Lote de {targetType === "cream" ? "crema" : "recepción"} *</Label>
                <Select value={form.reception_id} onValueChange={(v) => setForm({ ...form, reception_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="correction-reception"><SelectValue placeholder="Seleccionar lote" /></SelectTrigger>
                  <SelectContent>
                    {targetType === "cream"
                      ? lotOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.folio} · {r.supplier_name} · {r.kg?.toLocaleString("es-MX")} kg</SelectItem>)
                      : lotOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.folio} · {r.supplier_name} · {r.liters?.toLocaleString("es-MX")} L</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Resultado final</Label>
                <Input value={form.final_result} onChange={(e) => setForm({ ...form, final_result: e.target.value })} className="mt-1.5" placeholder="Ej. pH 6.6 estabilizado" />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 flex items-center gap-1.5">
                  <FlaskRound className="w-3.5 h-3.5" /> Insumos aplicados
                </Label>
                <button type="button" onClick={addRow} data-testid="add-insumo-btn" className="text-xs text-[#1E3A8A] hover:underline font-medium flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Agregar insumo
                </button>
              </div>

              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      {["Insumo", "Kg usados", "Precio $/kg", "Costo", ""].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {computedRows.map((r, i) => (
                      <tr key={i} className="border-b border-stone-100 last:border-0">
                        <td className="px-3 py-2">
                          <Select value={r.code} onValueChange={(v) => updateRow(i, "code", v)}>
                            <SelectTrigger data-testid={`insumo-select-${i}`} className="h-9"><SelectValue placeholder="Elegir..." /></SelectTrigger>
                            <SelectContent>
                              {catalog.map((c) => (
                                <SelectItem key={c.code} value={c.code}>
                                  {c.name} <span className="text-stone-400 ml-1">· ${c.price_per_kg}/kg</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          <Input type="number" step="0.01" value={r.kg} onChange={(e) => updateRow(i, "kg", e.target.value)} className="h-9 w-28" data-testid={`insumo-kg-${i}`} />
                        </td>
                        <td className="px-3 py-2 text-sm tabular-nums text-stone-700">{r.price ? `$${r.price}` : "—"}</td>
                        <td className="px-3 py-2 text-sm tabular-nums font-medium text-stone-900">{r.cost ? moneyFmt(r.cost) : "—"}</td>
                        <td className="px-3 py-2">
                          {rows.length > 1 && (
                            <button type="button" onClick={() => removeRow(i)} className="text-stone-400 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-stone-50 border-t border-stone-200">
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-right text-sm font-outfit font-semibold text-stone-700">Costo total de corrección</td>
                      <td className="px-3 py-3 text-base font-outfit font-bold tabular-nums text-[#1E3A8A]" data-testid="correction-total">{moneyFmt(total)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-stone-500 mt-2">
                {targetType === "cream"
                  ? "Este costo se registra como corrección acumulada del lote (no afecta lo que se le debe al proveedor)."
                  : "Este costo se sumará automáticamente al costo del lote (visible en la trazabilidad 360° del lote)."}
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-stone-200">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="correction-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar corrección</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full" data-testid="corrections-table">
          <thead>
            <tr className="bg-stone-100 border-b border-stone-200">
              {["Fecha", "Lote", "Insumos", "Costo total", "Responsable", "Acciones"].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-stone-500">Sin correcciones.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3 text-sm text-stone-700">{new Date(c.created_at).toLocaleString("es-MX")}</td>
                <td className="px-4 py-3 text-sm font-medium text-[#1E3A8A]">
                  <div className="flex items-center gap-1.5">
                    <Wrench className="w-3.5 h-3.5" />{c.reception_folio}
                    {c.target_type === "cream" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase">Crema</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">
                  {c.items && c.items.length > 0
                    ? c.items.map((it) => `${it.name} (${it.kg} kg)`).join(" · ")
                    : `Base ${c.base_kg ?? 0} kg + Caseína ${c.caseina_kg ?? 0} kg`}
                </td>
                <td className="px-4 py-3 text-sm tabular-nums font-medium text-stone-900">{moneyFmt(c.total_cost)}</td>
                <td className="px-4 py-3 text-sm text-stone-700">{c.responsible}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => navigate(`/trazabilidad?lote=${encodeURIComponent(c.reception_folio)}`)} title="Ver trazabilidad" className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => setToDelete(c)} data-testid={`delete-corr-${c.id}`} title="Eliminar" className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar corrección?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.target_type === "cream"
                ? `Se quitará ${moneyFmt(toDelete?.total_cost)} de la corrección acumulada del lote ${toDelete?.reception_folio} (no afecta lo que se le debe al proveedor).`
                : `Se restará ${moneyFmt(toDelete?.total_cost)} del costo del lote ${toDelete?.reception_folio}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { try { await api.delete(`/corrections/${toDelete.id}`); toast.success("Corrección eliminada"); setToDelete(null); load(); } catch { toast.error("Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
